// ./src/components/features/CreateEventWizard/components/WebsiteNudgeCard.tsx
//
// "Not on your event page yet" — the finish screen's catch for a host who finished before the website step (ROADMAP A2b
// #62 P6(d), owner 2026-09-30: a sign-up list made in the wizard never reached the site). Lists each chosen feature that
// HAS something to show and no block on any page, with one button that adds them all to the home page — the website
// step's own `addWebsiteBlocks`, so the two cannot add blocks differently.
//
// ⓘ Renders NOTHING when everything is on the page, and nothing when it cannot tell (a failed read): this card is an
//   offer, and "your sign-up list is missing" said over a read that failed would be a claim nobody checked.
// ⚠ A failed ADD is said here, with what was added before it.

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { eventFeaturesOn } from '@/config/eventSetup.generated';
import { Event, EventFeatureUsage, EventService } from '@/utils/api';
import { AppIcon, ErrorAlert } from '@/components/generic';

import { WebsiteBlockRow, featureBlocksMissing, planWebsiteBlocks } from '../websiteBlocks';
import { addWebsiteBlocks, readWebsiteState } from '../websiteBlocksApply';

export interface WebsiteNudgeCardProps {
  event: Event;
  /** Blocks the host SAW on the website step and left unticked — a choice, never nagged about. */
  declined?: readonly string[];
  /** #140 — the Sign-up list is the event's GIFT list: named and titled "Gift list", as the wizard's own steps do. */
  giftList?: boolean;
}

export const WebsiteNudgeCard: React.FC<WebsiteNudgeCardProps> = ({ event, declined = [], giftList = false }) => {
  const { t } = useTranslation(['events', 'common']);
  // The gift list's name, wherever the Sign-up list would be named.
  const giftTitle = (key: string): string | null =>
    key === 'signup' && giftList ? t('events:wizard.gifts.listBlockTitle', { defaultValue: 'Gift list' }) : null;
  const [missing, setMissing] = useState<WebsiteBlockRow[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<OpResult | null>(null);

  const check = useCallback(async () => {
    if (!event.id) {
      return;
    }

    const [site, usageRes] = await Promise.all([readWebsiteState(event.id), EventService.featureUsage(event.id)]);

    if (site instanceof OpResult || !usageRes.didSucceed()) {
      setMissing([]);

      return;
    }

    const usage = usageRes.getDataFirst() as EventFeatureUsage | undefined;
    const rows = planWebsiteBlocks({
      featuresOn: eventFeaturesOn(event.settings ?? null),
      withData: usage?.withData ?? [],
      // ⓘ Feature blocks only are nudged about — the About block's description is written on the website step.
      hasDescription: false,
      hasStart: !!event.startsAt,
      hasVenue: !!event.venueId,
      // ⓘ Feature blocks only are nudged about — the Schedule block is offered on the website step.
      hasSessions: false,
      onSite: site.onSite,
    });

    setMissing(featureBlocksMissing(rows, declined));
    // `declined` is read as given at mount (the wizard's final ticks); a new array each render must not re-read the site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, event.settings, event.startsAt, event.venueId, event.scheduleMode]);

  useEffect(() => {
    void check();
  }, [check]);

  const add = async () => {
    if (!event.id) {
      return;
    }

    setAdding(true);
    setError(null);

    const result = await addWebsiteBlocks(
      event.id,
      missing.map((r) => r.option.key),
      (key, fallback) => giftTitle(key) ?? t(`events:wizard.website.blockTitle.${key}`, { defaultValue: fallback }),
      (key, fallback) => t(`events:wizard.website.blockSubtitle.${key}`, { defaultValue: fallback, title: event.title ?? '' }),
      t('events:wizard.website.homeName', { defaultValue: 'Home' }),
    );

    setAdding(false);
    setError(result.error);
    await check();
  };

  if (missing.length === 0 && !error) {
    return null;
  }

  const names = missing.map((r) => giftTitle(r.option.key) ?? t(`events:wizard.website.block.${r.option.key}`, { defaultValue: r.option.label })).join(', ');

  return (
    <Alert variant="light" color="yellow" w="100%" icon={<AppIcon iconName="LayoutDashboard" size={18} />}>
      <Stack gap="xs" align="flex-start">
        {missing.length > 0 ? (
          <Text size="md" ta="left">
            {t('events:wizard.done.notOnPage', {
              defaultValue: 'Not on your event page yet, so guests cannot see it: {{names}}.',
              names,
            })}
          </Text>
        ) : null}
        {error ? (
          <ErrorAlert error={error} title={t('events:wizard.done.addBlocksFailed', { defaultValue: 'Could not add it to your event page' })} page="Create event" operation="pageWidgetCreate" />
        ) : null}
        {missing.length > 0 ? (
          <Button variant="light" size="md" loading={adding} leftSection={<AppIcon iconName="Plus" size={16} />} onClick={() => void add()}>
            {t('events:wizard.done.addToPage', { defaultValue: 'Add to your event page' })}
          </Button>
        ) : null}
      </Stack>
    </Alert>
  );
};
